import { defineStore } from 'pinia';
import { useAnalytics } from '~/composables/useAnalytics';
import { useCurrencyConverter } from '~/composables/useCurrencyConverter';
import {
  ESubscriptionStatus,
  EWebSocketPrivateChannels,
  type ISsCentrifugeBalanceChannel,
  type ISsCentrifugeGroupsChannel,
  type ISsCentrifugeMaxBetChannel,
  type IWebSocketChannel,
  type IWebSocketMessage,
  type IWebSocketMessageExtended,
  type TSubscribeStatus,
} from '~/types/softswiss/ssCentrifuge';
import {
  ESsPlayerBonusStage,
  type ISsPlayerBonuses,
  type ISsPlayerFreespins,
} from '~/types/softswiss/ssPlayerBonuses';
import { ETriggerId } from '~/types/toasts';
import { formatDateAndTime } from '~/utils/date/helpers';
import { useStoreGamePageData } from '../client/gamePageData';
import { useStoreUbidex } from '../client/ubidex';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { useStoreToasts } from '../strapi/toasts';
import { useStoreSsAccounts } from './accounts';
import { useStoreSsLootboxes } from './lootboxes';
import { useStoreSsPlayer } from './player';
import { useStoreSsPlayerBonuses } from './playerBonuses';
import { useStoreSsPlayerPayments } from './playerPayments';
import { useStoreSsTournaments } from './tournaments';
import { useStoreTransactionHistory } from './transactionHistory';
import type { ISsPlayerCompPoints } from '~/types/softswiss/ssPlayerCompPoints';
import type { ISsPlayerPayments } from '~/types/softswiss/ssPlayerPayments';
import type { ISsTournamentsNotifications } from '~/types/softswiss/ssTournamentNotifications';

const channelsToSubscribe = [
  EWebSocketPrivateChannels.bonuses,
  EWebSocketPrivateChannels.compoints,
  EWebSocketPrivateChannels.freespins,
  EWebSocketPrivateChannels.payments,
  EWebSocketPrivateChannels.tournaments,
  EWebSocketPrivateChannels.tournamentsNotifications,
  EWebSocketPrivateChannels.groups,
  EWebSocketPrivateChannels.balance,
  EWebSocketPrivateChannels.gameLimits,
];

export const useStoreSsCentrifuge = defineStore('ssCentrifuge', {
  state: () => ({
    messages: [] as IWebSocketMessageExtended[],
    webSocketInstance: { subscribers: {} } as IWebSocketChannel,
    subscribers: [] as EWebSocketPrivateChannels[],
    status: ESubscriptionStatus.noData as ESubscriptionStatus,
    subscribeStatus: {} as TSubscribeStatus,
    ssWebsocketUid: '1',
    requestTimeout: 0,
  }),

  getters: {
    socket(): WebSocket | undefined {
      return this.webSocketInstance.socket;
    },
  },
  actions: {
    async subscribeChannels() {
      channelsToSubscribe.forEach((channel) => {
        if (!this.subscribers.includes(channel)) {
          this.subscribers.push(channel);
          this.subscribeStatus[channel] = ESubscriptionStatus.subscribeInProgress;
        }
      });

      await this.subscribePrivateChannels();
    },

    /**
     * function for subscribe channel
     **/
    async subscribePrivateChannels(): Promise<boolean> {
      const runtimeConfig = useRuntimeConfig();
      if (!import.meta.browser || runtimeConfig.public.DISABLE_SS_CENTRIFUGE) {
        console.error(
          'SSR BLOCK! action ssCentrifuge/subscribePrivateChannels can be call only from browser!'
        );
        return false;
      }

      if (!useStoreSsPlayer().isPlayerAuthorised) {
        return false;
      }

      const unsubscribedChannels = this.subscribers.filter(
        (channel: EWebSocketPrivateChannels) =>
          this.subscribeStatus[channel] !== ESubscriptionStatus.subscribed
      );

      if (!unsubscribedChannels.length) {
        return false;
      }

      if (
        this.subscribers.every(
          (channel) => this.subscribeStatus[channel] !== ESubscriptionStatus.subscribed
        )
      ) {
        await this.closeWsConnection();
        await this.createWsConnection();
      }

      this.subscribeSsChannel(unsubscribedChannels);

      unsubscribedChannels.forEach((channel) => {
        this.webSocketInstance.subscribers[channel] = (message: IWebSocketMessage) => {
          if (message.method === 'message' && message.body.data) {
            if (channel !== EWebSocketPrivateChannels.tournaments) {
              this.messages.push({
                ...message,
                channelName: channel,
                isPrivateChannel: true,
              });
            }

            switch (channel) {
              case EWebSocketPrivateChannels.bonuses:
                this.privatChannelBonusSubscriber(message.body.data as ISsPlayerBonuses);
                break;
              case EWebSocketPrivateChannels.compoints:
                useStoreSsPlayer().updateUserCompPoints(message.body.data as ISsPlayerCompPoints);
                break;
              case EWebSocketPrivateChannels.freespins:
                useStoreSsPlayerBonuses().saveDataIfNeeded({
                  newItems: [message.body.data as ISsPlayerFreespins],
                  stateKey: 'freespins',
                });
                break;
              case EWebSocketPrivateChannels.payments:
                this.privatChannelPaymentsSubscriber(message.body.data as ISsPlayerPayments);
                break;
              case EWebSocketPrivateChannels.tournaments:
                // @ts-ignore
                useStoreSsTournaments().addTournamentStatus(message.body.data);
                break;
              case EWebSocketPrivateChannels.tournamentsNotifications:
                this.privatChannelTournamentsNotificationsSubscriber(
                  // @ts-ignore
                  message.body.data as ISsTournamentsNotifications
                );
                break;
              case EWebSocketPrivateChannels.groups:
                useStoreSsPlayer().updateGroups(message.body.data as ISsCentrifugeGroupsChannel[]);
                break;
              case EWebSocketPrivateChannels.balance:
                useStoreSsAccounts().updatePlayerBalance(
                  message.body.data as ISsCentrifugeBalanceChannel
                );
                break;
              case EWebSocketPrivateChannels.gameLimits:
                useStoreGamePageData().setMaxBetRestrictedMessage(
                  message.body.data as ISsCentrifugeMaxBetChannel
                );
                break;
              default:
                console.warn('Wrong message format');
            }
          }
        };
      });

      return true;
    },

    /**
     * function for websocket subscribe channel
     */
    subscribeSsChannel(channels: EWebSocketPrivateChannels[]) {
      const runtimeConfig = useRuntimeConfig();
      if (!import.meta.browser || runtimeConfig.public.DISABLE_SS_CENTRIFUGE) {
        return false;
      }

      const response = channels.map((channel) => {
        this.subscribeStatus[channel] = ESubscriptionStatus.subscribed;

        const result = {
          method: 'subscribe',
          params: { channel: `${channel}#${useStoreSsPlayer().playerId}` },
          uid: this.ssWebsocketUid,
        };

        this.ssWebsocketUid = String(Number(this.ssWebsocketUid) + 1);

        return result;
      });

      this.webSocketInstance.socket?.send(JSON.stringify(response));
    },

    /**
     *  function for create websocket connection
     **/
    createWsConnection(): Promise<boolean> {
      const runtimeConfig = useRuntimeConfig();
      if (!import.meta.browser || runtimeConfig.public.DISABLE_SS_CENTRIFUGE) {
        return false as any;
      }

      return new Promise((resolve) => {
        if (
          this.status !== ESubscriptionStatus.disconnected &&
          this.status !== ESubscriptionStatus.noData &&
          this.status !== ESubscriptionStatus.error
        ) {
          return resolve(true);
        }

        const socket = new WebSocket(
          `${
            (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host
          }/cent/connection/websocket`
        );
        // const socket = new WebSocket(`wss://dev.7bitcasino.com/cent/connection/websocket`);

        this.status = ESubscriptionStatus.connectInProgress;

        socket.onopen = () => {
          this.status = ESubscriptionStatus.connected;

          if (useStoreSsPlayer().isPlayerAuthorised) {
            const userSetting = useStoreSsPlayer().playerSettings;

            socket.send(
              JSON.stringify([
                {
                  method: 'connect',
                  params: {
                    user: `${userSetting.cent.user}`,
                    info: '',
                    timestamp: `${userSetting.cent.timestamp}`,
                    token: `${userSetting.cent.token}`,
                  },
                  uid: this.ssWebsocketUid,
                },
              ])
            );
          } else {
            socket.send(
              JSON.stringify([
                {
                  method: 'connect',
                  params: {
                    user: '',
                    info: '',
                    timestamp: '1609862146',
                    token: '4a14cb317e53e557e158897f8b12bde83541f02e68d3692e6c1587619ccf7a17',
                  },
                  uid: this.ssWebsocketUid,
                },
              ])
            );
          }

          this.ssWebsocketUid = String(Number(this.ssWebsocketUid) + 1);
          this.webSocketInstance.socket = socket;

          resolve(true);
        };

        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as IWebSocketMessage | IWebSocketMessage[];

            if (message instanceof Array) {
              message.forEach((m) => {
                // @ts-ignore
                const channel: EWebSocketPrivateChannels = m.body?.channel?.split('#')[0];
                this.webSocketInstance.subscribers[channel]?.(m);
              });
            } else {
              // @ts-ignore
              const channel: EWebSocketPrivateChannels = message.body?.channel?.split('#')[0];
              this.webSocketInstance.subscribers[channel]?.(message);
            }
          } catch {
            console.error(new Error('[error] WebSocket ERORR'));
          }
        };

        socket.onclose = async () => {
          if (this.status === ESubscriptionStatus.error) {
            if (this.requestTimeout < 60000) {
              this.requestTimeout = this.requestTimeout + 5000;
            }

            await this.closeWsConnection();
            setTimeout(async () => {
              await this.subscribeChannels();
            }, this.requestTimeout);
            return;
          }

          this.status = ESubscriptionStatus.disconnected;
          this.webSocketInstance.subscribers = {};
          Object.keys(this.subscribeStatus).forEach((channel) => {
            this.subscribeStatus[channel as EWebSocketPrivateChannels] =
              ESubscriptionStatus.disconnected;
          });

          await this.createWsConnection();

          if (useStoreSsPlayer().isPlayerAuthorised) {
            this.subscribePrivateChannels();
          }
        };

        socket.onerror = () => {
          this.status = ESubscriptionStatus.error;
          console.error(new Error('SS CENTRIFUGE CONNECTION ERROR'));
        };
      });
    },

    closeWsConnection(): Promise<boolean> {
      const runtimeConfig = useRuntimeConfig();
      if (!import.meta.browser || runtimeConfig.public.DISABLE_SS_CENTRIFUGE) {
        return false as any;
      }

      return new Promise((resolve) => {
        const socket = this.webSocketInstance.socket;

        if (socket) {
          if (socket.readyState === socket.CLOSED) {
            return resolve(true);
          }

          socket.onclose = () => {
            this.status = ESubscriptionStatus.disconnected;
            this.webSocketInstance.subscribers = {};
            Object.keys(this.subscribeStatus).forEach((channel) => {
              this.subscribeStatus[channel as EWebSocketPrivateChannels] =
                ESubscriptionStatus.unsubscribed;
            });
            resolve(true);
          };

          socket.close();
          return true;
        }
        resolve(true);
      });
    },

    async unsubscribeChannels() {
      channelsToSubscribe.forEach((channel) => {
        this.subscribers = this.subscribers.filter((s) => s !== channel);
        this.subscribeStatus[channel] = ESubscriptionStatus.unsubscribed;
      });

      await this.closeWsConnection();
      await this.createWsConnection();
      await this.subscribePrivateChannels();
    },

    privatChannelTournamentsNotificationsSubscriber(data: ISsTournamentsNotifications) {
      const isTournamentStartingSoon = data.event === 'tournament_starts_soon';
      const isTournamentStarted = data.event === 'tournament_started';
      const isPlayerKickedFromLeaderboard = data.event === 'player_kicked_from_leaderboard';

      if (isTournamentStartingSoon) {
        useStoreToasts().add(ETriggerId.TournamentStartingSoon, {
          preMessage: `${data.title}`,
        });
      }

      if (isTournamentStarted) {
        useStoreToasts().add(ETriggerId.TournamentStarted, {
          preMessage: `${data.title}`,
          postMessage: `${formatDateAndTime(data.end_at)}`,
        });
      }

      if (isPlayerKickedFromLeaderboard) {
        useStoreToasts().add(ETriggerId.PlayerKickedFromLeaderboard, {
          preMessage: `${data.award_place}`,
          postMessage: `${data.title}`,
        });
      }
    },

    privatChannelPaymentsSubscriber(data: ISsPlayerPayments) {
      if (data.action === 'deposit') {
        if (data.success || data.state === 'accepted') {
          const storeCurrencyMenu = useStoreCurrencyMenu();

          const { convertToFormattedAmount } = useCurrencyConverter();
          const { formattedAmount, formattedCurrency } = convertToFormattedAmount(
            data.amount_cents,
            data.currency
          );

          if (storeCurrencyMenu.isCryptoCurency(data.currency)) {
            useAnalytics().deposit(true);
          }

          useStoreToasts().add(ETriggerId.DepositSuccess, {
            preMessage: `<b>${formattedAmount}</b> ${formattedCurrency}`,
          });
        }

        useStoreSsPlayerPayments().addPayment(data);
        // useStoreSsAccounts().loadPlayerBalance({ force: true });
        useStoreTransactionHistory().loadPayments({ force: true });
        useStoreSsLootboxes().loadLootboxes();
      } else if (data.action === 'cashout') {
        const { convertToFormattedAmount } = useCurrencyConverter();
        const { formattedAmount, formattedCurrency } = convertToFormattedAmount(
          data.amount_cents,
          data.currency
        );

        if (data.success) {
          useStoreToasts().add(ETriggerId.CashoutSuccess, {
            preMessage: `${formattedAmount} ${formattedCurrency}`,
          });
        } else {
          useStoreToasts().add(ETriggerId.CashoutFail);
        }
      }
    },

    privatChannelBonusSubscriber(data: ISsPlayerBonuses) {
      useStoreSsPlayerBonuses().saveDataIfNeeded({
        newItems: [data],
        stateKey: 'bonuses',
      });
      useStoreSsAccounts().loadPlayerBalance({ force: true });

      if (data.stage === ESsPlayerBonusStage.ISSUED) {
        useStoreUbidex().sendEvent({
          event: 'other',
          amount: data.amount_cents,
          currency: data.currency,
        });
      }
    },
  },
});
